import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import * as SunCalc from 'suncalc';
import { getMoonPhaseDetails } from '../../../../utils/astronomy';
import { getLocalDateTimeParts, localDateTimeToUtcMs } from '../../../../services/PoiDataService';

export class SolarEphemerisWidget implements WidgetRenderer {
  private formatTime(d: Date | null | undefined, tz?: string): string {
    if (!d || isNaN(d.getTime())) return '--:--';
    try {
      return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...(tz ? { timeZone: tz } : {})
      });
    } catch {
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  }

  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Solar & Lunar Calendar & Ephemeris';
    const opts = widget.options || {};
    const isLegacyEphem = widget.type === 'solar-ephemeris' && !opts.componentMode;
    const compMode = opts.componentMode || (isLegacyEphem ? 'ephemeris' : 'combined');
    const showIllum = opts.showIllumination !== false;

    const tz = ctx.data?.timezone || undefined;
    const now = new Date();
    const nowParts = tz ? getLocalDateTimeParts(now.getTime(), tz) : null;
    const curYear = nowParts ? nowParts.year : now.getFullYear();
    const curMonth = nowParts ? (nowParts.month - 1) : now.getMonth();
    const todayDateNum = nowParts ? nowParts.day : now.getDate();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const curMonthName = monthNames[curMonth];

    // Initial Ephemeris for today in POI's timezone
    const lat = ctx.lat || 47.6;
    const lon = ctx.lon || -122.3;
    const todayNoon = tz ? new Date(localDateTimeToUtcMs(curYear, curMonth + 1, todayDateNum, 12, 0, tz)) : now;
    const sTimes = SunCalc.getTimes(todayNoon, lat, lon);
    const moon = getMoonPhaseDetails(todayNoon);

    const sunrise = this.formatTime(sTimes.sunrise, tz);
    const sunset = this.formatTime(sTimes.sunset, tz);
    const goldenMorn = `${this.formatTime(sTimes.sunrise, tz)} - ${this.formatTime(sTimes.goldenHourEnd, tz)}`;
    const goldenEve = `${this.formatTime(sTimes.goldenHour, tz)} - ${this.formatTime(sTimes.sunset, tz)}`;
    const blueEve = this.formatTime(sTimes.dusk, tz);

    // 1. Calendar Grid HTML
    let calendarHtml = '';
    if (compMode !== 'ephemeris') {
      const firstDay = new Date(curYear, curMonth, 1).getDay();
      const daysInMonth = new Date(curYear, curMonth + 1, 0).getDate();

      let cellsHtml = '';
      for (let p = 0; p < firstDay; p++) {
        cellsHtml += `<div style="background: transparent; min-height: 38px;"></div>`;
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const dayDate = new Date(curYear, curMonth, day, 12, 0, 0);
        const dayMoon = getMoonPhaseDetails(dayDate);
        const isToday = day === todayDateNum;
        const illumPct = parseInt(dayMoon.illumination, 10) || 50;

        cellsHtml += `
          <div 
            class="solar-lunar-cal-cell ${isToday ? 'today' : ''}" 
            data-day="${day}"
            data-year="${curYear}"
            data-month="${curMonth}"
            style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4px 2px; border-radius: 6px; background: rgba(30, 41, 59, 0.45); border: 1px solid ${isToday ? 'rgba(56, 189, 248, 0.6)' : 'rgba(255, 255, 255, 0.06)'}; min-height: 38px; cursor: pointer; user-select: none; transition: all 0.15s ease;"
            title="Inspect ${curMonthName} ${day} (${dayMoon.name})"
          >
            <span class="day-num" style="font-size: 0.68rem; font-weight: ${isToday ? '800' : '600'}; color: ${isToday ? '#38bdf8' : '#94a3b8'};">${day}</span>
            <span style="font-size: 0.95rem; margin: 1px 0; line-height: 1;">${dayMoon.icon}</span>
            ${showIllum ? `<span style="font-size: 0.58rem; color: #64748b;">${illumPct}%</span>` : ''}
          </div>
        `;
      }

      calendarHtml = `
        <div style="margin-bottom: 10px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
            <span style="font-size: 0.8rem; font-weight: 700; color: #f8fafc;">${curMonthName} ${curYear}</span>
            <span style="font-size: 0.65rem; color: #64748b;">Tap a date to inspect ephemeris</span>
          </div>
          <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; text-align: center;">
            <div style="font-size: 0.6rem; font-weight: 700; color: #64748b; padding: 2px 0;">SU</div>
            <div style="font-size: 0.6rem; font-weight: 700; color: #64748b; padding: 2px 0;">MO</div>
            <div style="font-size: 0.6rem; font-weight: 700; color: #64748b; padding: 2px 0;">TU</div>
            <div style="font-size: 0.6rem; font-weight: 700; color: #64748b; padding: 2px 0;">WE</div>
            <div style="font-size: 0.6rem; font-weight: 700; color: #64748b; padding: 2px 0;">TH</div>
            <div style="font-size: 0.6rem; font-weight: 700; color: #64748b; padding: 2px 0;">FR</div>
            <div style="font-size: 0.6rem; font-weight: 700; color: #64748b; padding: 2px 0;">SA</div>
            ${cellsHtml}
          </div>
        </div>
      `;
    }

    // 2. Ephemeris Details HTML
    let ephemHtml = '';
    if (compMode !== 'calendar') {
      ephemHtml = `
        <div id="widget-${widget.id}-ephem-wrap">
          <div id="widget-${widget.id}-date-badge" style="display: none; align-items: center; justify-content: space-between; padding: 4px 8px; margin-bottom: 8px; background: rgba(56, 189, 248, 0.12); border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 6px;">
            <span class="badge-text" style="font-size: 0.68rem; font-weight: 700; color: #38bdf8;">📅 Ephemeris</span>
            <button type="button" class="btn-reset-ephem" style="background: rgba(56, 189, 248, 0.25); border: 1px solid rgba(56, 189, 248, 0.5); color: #fff; font-size: 0.62rem; font-weight: 700; padding: 1px 6px; border-radius: 4px; cursor: pointer;">Today</button>
          </div>
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;" id="widget-${widget.id}-tiles">
            <div style="background: rgba(15,23,42,0.6); padding: 8px 6px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.06); text-align: center;">
              <div style="font-size: 0.62rem; color: #f97316; font-weight: 700;">SUNRISE</div>
              <div class="val-sunrise" style="font-size: 0.95rem; font-weight: 700; color: #f8fafc; font-family: monospace;">${sunrise}</div>
            </div>
            <div style="background: rgba(15,23,42,0.6); padding: 8px 6px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.06); text-align: center;">
              <div style="font-size: 0.62rem; color: #fbbf24; font-weight: 700;">SUNSET</div>
              <div class="val-sunset" style="font-size: 0.95rem; font-weight: 700; color: #f8fafc; font-family: monospace;">${sunset}</div>
            </div>
            <div style="background: rgba(15,23,42,0.6); padding: 8px 6px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.06); text-align: center;">
              <div style="font-size: 0.62rem; color: #a855f7; font-weight: 700;">MOON (${parseInt(moon.illumination, 10)}%)</div>
              <div class="val-moon" style="font-size: 0.85rem; font-weight: 700; color: #f8fafc;">${moon.icon} ${moon.name}</div>
            </div>
          </div>
          <div style="display: flex; justify-content: space-between; margin-top: 6px; padding: 6px 10px; background: rgba(15,23,42,0.4); border-radius: 6px; border: 1px solid rgba(255,255,255,0.04); font-size: 0.68rem;">
            <span style="color: #94a3b8;">🌅 Golden Hour: <strong class="val-golden" style="color: #fbbf24;">${goldenMorn} · ${goldenEve}</strong></span>
            <span style="color: #94a3b8;">🌌 Blue Hour: <strong class="val-blue" style="color: #38bdf8;">${blueEve}</strong></span>
          </div>
        </div>
      `;
    }

    return `
      <div id="widget-${widget.id}" class="metric-card" style="background: linear-gradient(145deg, #13151f 0%, #0d0e15 100%); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;">
          <div style="font-size: 0.8rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px;">🌙☀️ ${title}</div>
          <span style="font-size: 0.62rem; font-weight: 700; color: #38bdf8; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.3); padding: 1px 6px; border-radius: 4px;">Ephemeris</span>
        </div>
        ${compMode === 'calendar' ? calendarHtml : ''}
        ${compMode === 'ephemeris' ? ephemHtml : ''}
        ${compMode === 'combined' ? `
          ${calendarHtml}
          <div style="margin: 10px 0; border-top: 1px solid rgba(255, 255, 255, 0.08);"></div>
          ${ephemHtml}
        ` : ''}
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const container = document.getElementById(`widget-${widget.id}`);
    if (!container) return;

    const lat = ctx.lat || 47.6;
    const lon = ctx.lon || -122.3;
    const tz = ctx.data?.timezone || undefined;
    const cells = container.querySelectorAll<HTMLElement>('.solar-lunar-cal-cell');
    const dateBadge = container.querySelector<HTMLElement>(`#widget-${widget.id}-date-badge`);
    const badgeText = dateBadge?.querySelector<HTMLElement>('.badge-text');
    const resetBtn = dateBadge?.querySelector<HTMLButtonElement>('.btn-reset-ephem');
    const valSunrise = container.querySelector<HTMLElement>('.val-sunrise');
    const valSunset = container.querySelector<HTMLElement>('.val-sunset');
    const valMoon = container.querySelector<HTMLElement>('.val-moon');
    const valGolden = container.querySelector<HTMLElement>('.val-golden');
    const valBlue = container.querySelector<HTMLElement>('.val-blue');

    const updateEphemerisView = (targetDate: Date, isCustom: boolean) => {
      const sTimes = SunCalc.getTimes(targetDate, lat, lon);
      const moon = getMoonPhaseDetails(targetDate);
      const sunrise = this.formatTime(sTimes.sunrise, tz);
      const sunset = this.formatTime(sTimes.sunset, tz);
      const goldenMorn = `${this.formatTime(sTimes.sunrise, tz)} - ${this.formatTime(sTimes.goldenHourEnd, tz)}`;
      const goldenEve = `${this.formatTime(sTimes.goldenHour, tz)} - ${this.formatTime(sTimes.sunset, tz)}`;
      const blueEve = this.formatTime(sTimes.dusk, tz);

      if (valSunrise) valSunrise.textContent = sunrise;
      if (valSunset) valSunset.textContent = sunset;
      if (valMoon) valMoon.textContent = `${moon.icon} ${moon.name}`;
      if (valGolden) valGolden.textContent = `${goldenMorn} · ${goldenEve}`;
      if (valBlue) valBlue.textContent = blueEve;

      if (dateBadge && badgeText) {
        if (isCustom) {
          dateBadge.style.display = 'flex';
          const dStr = targetDate.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            ...(tz ? { timeZone: tz } : {})
          });
          badgeText.textContent = `📅 Selected Ephemeris: ${dStr}`;
        } else {
          dateBadge.style.display = 'none';
        }
      }
    };

    cells.forEach(cell => {
      cell.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        cells.forEach(c => {
          c.style.background = 'rgba(30, 41, 59, 0.45)';
          c.style.borderColor = c.classList.contains('today') ? 'rgba(56, 189, 248, 0.6)' : 'rgba(255, 255, 255, 0.06)';
          c.style.boxShadow = 'none';
          c.style.transform = 'none';
        });

        cell.style.background = 'rgba(56, 189, 248, 0.25)';
        cell.style.borderColor = '#38bdf8';
        cell.style.boxShadow = '0 0 10px rgba(56, 189, 248, 0.4)';
        cell.style.transform = 'scale(1.05)';

        const day = parseInt(cell.getAttribute('data-day') || '1', 10);
        const year = parseInt(cell.getAttribute('data-year') || String(new Date().getFullYear()), 10);
        const month = parseInt(cell.getAttribute('data-month') || String(new Date().getMonth()), 10);
        const targetDate = tz
          ? new Date(localDateTimeToUtcMs(year, month + 1, day, 12, 0, tz))
          : new Date(year, month, day, 12, 0, 0);

        updateEphemerisView(targetDate, true);
      });
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        cells.forEach(c => {
          c.style.background = 'rgba(30, 41, 59, 0.45)';
          c.style.borderColor = c.classList.contains('today') ? 'rgba(56, 189, 248, 0.6)' : 'rgba(255, 255, 255, 0.06)';
          c.style.boxShadow = 'none';
          c.style.transform = 'none';
        });

        const nowParts = tz ? getLocalDateTimeParts(Date.now(), tz) : null;
        const resetDate = tz && nowParts
          ? new Date(localDateTimeToUtcMs(nowParts.year, nowParts.month, nowParts.day, 12, 0, tz))
          : new Date();
        updateEphemerisView(resetDate, false);
      });
    }
  }
}
